import { Dialog as DialogPrimitive } from "@base-ui/react/dialog";
import { X } from "lucide-react";
import * as React from "react";

import { Button } from "@/shared/components/primitives/button";
import { cn } from "@/shared/lib/utils";

function Dialog({ ...props }: DialogPrimitive.Root.Props) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}

function DialogTrigger({ ...props }: DialogPrimitive.Trigger.Props) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal({ ...props }: DialogPrimitive.Portal.Props) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DialogClose({ ...props }: DialogPrimitive.Close.Props) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

function DialogOverlay({ className, ...props }: DialogPrimitive.Backdrop.Props) {
  return (
    <DialogPrimitive.Backdrop
      data-slot="dialog-overlay"
      className={cn(
        "fixed inset-0 isolate z-50 bg-black/10 duration-100 data-open:animate-in data-open:fade-in-0 data-closed:animate-out data-closed:fade-out-0",
        className,
      )}
      {...props}
    />
  );
}

function DialogContent({
  className,
  children,
  size = "default",
  showCloseButton = true,
  ...props
}: DialogPrimitive.Popup.Props & {
  size?: "sm" | "default" | "lg" | "xl";
  showCloseButton?: boolean;
}) {
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Popup
        data-slot="dialog-content"
        data-size={size}
        className={cn(
          "group/dialog-content fixed top-1/2 left-1/2 z-50 flex w-full max-w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2 flex-col rounded-xl bg-popover p-2 text-popover-foreground ring-1 ring-foreground/10 duration-100 outline-none",
          // Two bounds, whichever bites first. A dialog is centred with `-translate-y-1/2`, so anything taller
          // than the screen loses its title AND its footer off both edges with no way to scroll to them — hence
          // the viewport bound, which keeps a deliberate, symmetric margin rather than hugging the edges. The
          // absolute bound stops a long form from stretching into a full-height column: past this height the
          // body scrolls, which reads far better than a dialog the size of the page.
          "max-h-[min(48rem,calc(100dvh-5rem))]",
          "data-[size=sm]:sm:max-w-sm data-[size=default]:sm:max-w-lg data-[size=lg]:sm:max-w-2xl data-[size=xl]:sm:max-w-4xl",
          "data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95",
          "sm:bg-default/60 supports-backdrop-filter:backdrop-blur-xs sm:p-2 sm:shadow-xs",
          className,
        )}
        {...props}
      >
        {/*
          A column of three regions: header, body, footer. `ColocaDialog` marks the body as the one that
          scrolls, so the header and the action row stay put while the content moves under them.

          `overflow-y-auto` here is a NARROW fallback, and it is worth being precise about when it fires,
          because it reads like the thing that scrolls a long form and it is not. The body is the only
          flexible child and it carries `min-h-0`, so it absorbs exactly the space left over and this box's
          content height equals its own — long content can never make THIS box scroll. What can is the
          non-shrinking regions alone (header + toolbar + footer) exceeding the card, which happens on a very
          short viewport: a landscape phone is 768px WIDE, so it takes the centred dialog rather than the
          drawer, on ~390px of height. Without this, the footer would leave the card with no way back to it.

          So: long body content is `DialogBody`'s business. Deleting this line does not fix a double
          scrollbar — that is a consumer declaring its own scroll region inside the body. See design-system.md
          rule 5.
        */}
        <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto rounded-lg border bg-popover p-6 shadow-dialog">
          {children}
        </div>
        {showCloseButton && (
          <DialogPrimitive.Close
            data-slot="dialog-close"
            render={<Button variant="ghost" className="absolute top-4 right-4" size="icon-sm" />}
          >
            <X />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Popup>
    </DialogPortal>
  );
}

function DialogHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div data-slot="dialog-header" className={cn("flex shrink-0 flex-col gap-1.5", className)} {...props} />
  );
}

/**
 * The scrolling middle region. Everything between the header and the footer belongs in here, so the action
 * row stays reachable however long the content is.
 *
 * The horizontal bleed is deliberate: `-mx-6 px-6` puts the scrollbar on the card's edge while keeping the
 * fields inset, so a focus ring has room instead of triggering a sideways scrollbar.
 */
function DialogBody({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-body"
      className={cn("-mx-6 min-h-0 flex-1 overflow-y-auto px-6 py-1", className)}
      {...props}
    />
  );
}

function DialogFooter({
  className,
  showCloseButton = false,
  children,
  ...props
}: React.ComponentProps<"div"> & {
  showCloseButton?: boolean;
}) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        // A flex item after the scrolling body, so it sits on the card's bottom edge without `sticky` or the
        // overlap that comes with it. `-mb-6` cancels the card's bottom padding; the `gap-5` above it is the
        // separation from the content, which must never look like the bar is resting on the last field.
        "-mx-6 -mb-6 flex shrink-0 flex-col-reverse gap-2 rounded-b-xl border-t border-dashed bg-muted/50 p-4 group-data-[size=sm]/alert-dialog-content:grid group-data-[size=sm]/alert-dialog-content:grid-cols-2 sm:flex-row sm:justify-end",
        className,
      )}
      {...props}
    >
      {children}
      {showCloseButton && <DialogPrimitive.Close render={<Button />}>Close</DialogPrimitive.Close>}
    </div>
  );
}

function DialogTitle({ className, ...props }: DialogPrimitive.Title.Props) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn("font-heading text-lg font-medium leading-none", className)}
      {...props}
    />
  );
}

function DialogDescription({ className, ...props }: DialogPrimitive.Description.Props) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn(
        "text-sm text-balance text-muted-foreground *:[a]:underline *:[a]:underline-offset-3 *:[a]:hover:text-foreground",
        className,
      )}
      {...props}
    />
  );
}

export {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
